import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AGENCIES } from '../mock';
import { fetchDossier, imageUrl, downloadImageUrl, API } from '../api';
import { Sentences } from '../lib/text';
import { ArrowLeft, MapPin, Calendar, Hash, FileText, Download, ExternalLink, Lock, Tag, Building2, Clock4, Loader2, Play, Image as ImageIcon, Languages, Sparkles, CheckCircle2 } from 'lucide-react';

export default function DossierPage() {
  const { id } = useParams();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const d = await fetchDossier(id);
        if (!cancel) setDossier(d);
      } catch (e) {
        if (!cancel) setError(e?.response?.status === 404 ? 'introuvable' : 'erreur');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  if (loading) return <div className="mx-auto max-w-[1200px] px-6 py-20 text-center text-3 inline-flex items-center gap-2 w-full justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>;
  if (error || !dossier) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 py-20 text-center">
        <div className="text-3 mb-3">Dossier introuvable</div>
        <Link to="/archives" className="text-cyan-300 inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Retour aux archives</Link>
      </div>
    );
  }
  const agency = AGENCIES.find(a => a.id === dossier.agency);
  const isCIA = dossier.agency === 'CIA';
  const backTo = isCIA ? '/cia' : '/archives';
  const backLabel = isCIA ? 'Toutes les déclassifications CIA' : 'Toutes les archives';

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-8">
      <Link to={backTo} className="text-3 hover:text-1 text-sm inline-flex items-center gap-1 mb-5"><ArrowLeft className="h-4 w-4" /> {backLabel}</Link>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="badge-classif" style={{ color: agency?.color }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: agency?.color }} /> {agency?.label}
            </span>
            <span className="mono text-[11px] text-3">{dossier.id}</span>
            <span className="mono text-[11px] px-2 py-0.5 rounded border border-amber-300/30 text-amber-200">{dossier.classification}</span>
            {dossier.type_en && <span className="mono text-[11px] px-2 py-0.5 rounded border border-cyan-300/30 text-cyan-200">{dossier.type_en}</span>}
          </div>
          <h1 className="serif text-2xl md:text-3xl leading-tight">{dossier.title}</h1>

          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 mono text-[11px] text-2">
            {dossier.date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Incident: {dossier.date}</span>}
            <span className="inline-flex items-center gap-1.5"><Clock4 className="h-3 w-3" /> Libéré: {dossier.release_date}</span>
            {dossier.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {dossier.location}</span>}
          </div>

          {dossier.image_url && (
            <div className="mt-5 relative rounded-lg overflow-hidden border border-white/10 bg-black">
              <img src={imageUrl(dossier.image_url)} alt={dossier.title} className="w-full max-h-[480px] object-cover" />
              <div className="absolute top-2 left-2 mono text-[10px] bg-black/70 px-2 py-0.5 rounded text-2">ÉVIDENCE · {dossier.id.split('-').slice(0, 2).join('-')}</div>
            </div>
          )}

          {/* Action bar — clearly visible buttons below the image */}
          <ActionBar dossier={dossier} />

          <div className="mt-5 glass rounded-lg p-5">
            <div className="mono text-[10px] tracking-widest text-cyan-300/90 uppercase mb-3">Résumé · Traduction française par IA</div>
            <Sentences text={dossier.summary || 'Résumé non disponible.'} className="text-1 leading-relaxed text-sm" />
          </div>

          {dossier.video_id && (
            <div className="mt-4 glass rounded-lg p-4">
              <div className="mono text-[10px] tracking-widest text-emerald-300/90 uppercase mb-2 inline-flex items-center gap-1.5"><Play className="h-3 w-3" /> Vidéo associée (DVIDS)</div>
              <div className="text-1 text-sm font-medium">{dossier.video_title || 'Vidéo officielle'}</div>
              <div className="flex flex-wrap gap-2 mt-2">
                <a href={`https://www.dvidshub.net/video/${dossier.video_id}`} target="_blank" rel="noreferrer" className="btn-primary px-3 py-1.5 rounded text-xs inline-flex items-center gap-1.5 font-medium">
                  <Play className="h-3.5 w-3.5" /> Voir la vidéo
                </a>
                <a href={`https://www.dvidshub.net/video/download/${dossier.video_id}`} target="_blank" rel="noreferrer" className="btn-ghost px-3 py-1.5 rounded text-xs inline-flex items-center gap-1.5">
                  <Download className="h-3.5 w-3.5" /> Télécharger
                </a>
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-5 space-y-3">
          <div className="glass-strong rounded-lg p-4">
            <div className="mono text-[10px] tracking-widest text-3 uppercase mb-2">Métadonnées</div>
            <Row icon={Building2} label="Agence" value={agency?.label} color={agency?.color} />
            <Row icon={Hash} label="ID" value={dossier.id} />
            <Row icon={Lock} label="Classification" value={dossier.classification} />
            {dossier.location && <Row icon={MapPin} label="Lieu" value={dossier.location} />}
            {dossier.date && <Row icon={Calendar} label="Date incident" value={dossier.date} />}
            <Row icon={Clock4} label="Date publication" value={dossier.release_date} />
            <Row icon={FileText} label="Fichier" value={dossier.filename} />
          </div>

          {dossier.tags && dossier.tags.length > 0 && (
            <div className="glass rounded-lg p-4">
              <div className="mono text-[10px] tracking-widest text-3 uppercase mb-2">Tags</div>
              <div className="flex flex-wrap gap-1">
                {dossier.tags.map(t => (
                  <span key={t} className="mono text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-2 inline-flex items-center gap-1">
                    <Tag className="h-2.5 w-2.5" /> {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="glass rounded-lg p-4">
            <div className="mono text-[10px] tracking-widest text-3 uppercase mb-2">Source officielle</div>
            <a href={dossier.source_url} target="_blank" rel="noreferrer" className="text-cyan-300 text-sm hover:text-cyan-200 inline-flex items-center gap-1.5 break-all">
              <ExternalLink className="h-3 w-3 shrink-0" /> {dossier.source_url}
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5 last:border-0">
      <div className="inline-flex items-center gap-1.5 text-3 text-xs shrink-0"><Icon className="h-3 w-3" /> {label}</div>
      <div className="text-1 text-xs text-right break-all" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}

function ActionBar({ dossier }) {
  const [translating, setTranslating] = useState(false);
  const [translatedReady, setTranslatedReady] = useState(false);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [chunks, setChunks] = useState({ done: 0, total: 0 });

  const STAGE_LABELS = {
    queued: 'En file d\'attente…',
    download: 'Téléchargement du PDF original depuis cia.gov…',
    extract: 'Extraction du texte…',
    ocr: 'Reconnaissance OCR des pages scannées…',
    vision_ocr: 'OCR IA-vision (texte extrait par GPT-4o)…',
    translate: 'Traduction française par IA…',
    render: 'Génération du PDF final avec logo Alliance Humaine…',
    done: 'Terminé',
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/translate/${dossier.id}/status`);
        const data = await r.json();
        if (!cancel) setTranslatedReady(!!data.ready);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [dossier.id]);

  const imageFilename = dossier.image_url ? dossier.image_url.replace('/api/images/', '') : '';

  async function handleTranslate() {
    setError(null);
    setTranslating(true);
    setStage('queued');
    setProgress(0);
    setChunks({ done: 0, total: 0 });
    try {
      // 1. First call: launches the job (202) or returns the PDF directly if cached (200)
      const firstRes = await fetch(`${API}/translate/${dossier.id}`);
      if (firstRes.status === 200) {
        // Cached: download immediately
        const blob = await firstRes.blob();
        triggerDownload(blob, `${dossier.id}_FR.pdf`);
        setTranslatedReady(true);
        setProgress(100);
        setStage('done');
        return;
      }
      if (firstRes.status !== 202) throw new Error(`HTTP ${firstRes.status}`);

      // 2. Poll status — up to 30 minutes for very large CIA scanned docs
      const startedAt = Date.now();
      const MAX_MS = 30 * 60 * 1000;
      let consecutiveFails = 0;
      while (Date.now() - startedAt < MAX_MS) {
        await new Promise(r => setTimeout(r, 3000));
        let s;
        try {
          s = await (await fetch(`${API}/translate/${dossier.id}/status`)).json();
          consecutiveFails = 0;
        } catch (netErr) {
          // Tolerate transient network blips
          consecutiveFails += 1;
          if (consecutiveFails > 5) throw netErr;
          continue;
        }
        setStage(s.stage || s.status);
        setProgress(s.progress ?? 0);
        setChunks({ done: s.chunks_done || 0, total: s.chunks_total || 0 });
        if (s.error) throw new Error(s.error);
        if (s.ready) {
          const blob = await (await fetch(`${API}/translate/${dossier.id}`)).blob();
          triggerDownload(blob, `${dossier.id}_FR.pdf`);
          setTranslatedReady(true);
          setProgress(100);
          setStage('done');
          return;
        }
      }
      throw new Error('Délai dépassé (30 min)');
    } catch (e) {
      console.error(e);
      setError("Échec de la génération : " + (e.message || 'inconnu'));
    } finally {
      setTranslating(false);
    }
  }

  function triggerDownload(blob, name) {
    const a = document.createElement('a');
    const objUrl = URL.createObjectURL(blob);
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  }

  return (
    <div className="mt-3 glass rounded-lg p-3 flex flex-wrap items-center gap-2">
      <span className="mono text-[10px] tracking-widest text-3 uppercase mr-1">Actions ·</span>

      <button
        onClick={handleTranslate}
        disabled={translating}
        className="px-3.5 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5 bg-cyan-500/90 hover:bg-cyan-400 text-slate-950 transition-colors disabled:opacity-60 disabled:cursor-wait"
      >
        {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        {translating ? 'Traduction en cours…' : (translatedReady ? 'Re-télécharger la traduction FR' : 'Traduction française complète (PDF)')}
        {translatedReady && !translating && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />}
      </button>

      {dossier.pdf_url && (
        <a
          href={dossier.pdf_url}
          target="_blank"
          rel="noreferrer"
          className="px-3.5 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5 bg-amber-500/90 hover:bg-amber-400 text-slate-950 transition-colors"
        >
          <Download className="h-3.5 w-3.5" /> PDF original (anglais)
        </a>
      )}

      {dossier.image_url && (
        <a
          href={downloadImageUrl(imageFilename)}
          className="px-3.5 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-1 border border-white/15 transition-colors"
        >
          <ImageIcon className="h-3.5 w-3.5" /> Télécharger l'image
        </a>
      )}

      {dossier.source_url && (
        <a
          href={dossier.source_url}
          target="_blank"
          rel="noreferrer"
          className="px-3.5 py-2 rounded-md text-xs font-medium inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-2 border border-white/10 transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Source {dossier.agency === 'CIA' ? 'cia.gov' : 'war.gov'}
        </a>
      )}

      {error && <span className="text-rose-400 text-xs ml-2">{error}</span>}
      {translating && (
        <div className="w-full mt-2 mono">
          <div className="flex items-center justify-between text-[10px] tracking-widest uppercase text-cyan-300/80 mb-1">
            <span>
              {STAGE_LABELS[stage] || stage || 'Initialisation…'}
              {stage === 'translate' && chunks.total > 0 && (
                <span className="text-amber-300/90 ml-2">
                  · segment {chunks.done}/{chunks.total}
                </span>
              )}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-3 text-[10px] mt-1">
            Téléchargement → OCR → traduction IA → rendu. Temps typique : 1-3 min pour les petits documents, 10-20 min pour les gros scans (50+ pages). Vous pouvez laisser la page ouverte.
          </div>
        </div>
      )}
    </div>
  );
}
